import { Dialog, Typography } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import Loader from 'components/Loader';
import { BatchRow } from 'containers/PosOrderDetailsPage/BatchRow';

import { postInventorySearch } from 'services/Inventory';

import { InventoryItem, InventoryProductItem, InventorySearchPayload } from 'types/inventory.interface';
import { POSAddItemToOrderPayload } from 'types/pos.interface';
import { handleApiErrors } from 'utils/errorUtils';
import { getStoreId } from 'utils/storageUtils';

import {
  TitleWrapper,
  CloseButton,
  BatchWrapper,
  ModalContainer,
} from './index.styled';

interface ItemDetailsModalProps {
  open: boolean;
  onClose: () => void;
  product: InventoryProductItem;
  onAddItemToOrderHandler: (payload: POSAddItemToOrderPayload) => void;
}

const BatchesModal: FC<ItemDetailsModalProps> = ({
  open,
  onClose,
  product,
  onAddItemToOrderHandler,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [batches, setBatches] = useState<InventoryItem[]>([]);

  const [searchParams] = useSearchParams();
  const productId = searchParams.get('productId');

  const getBatches = async (page: number, pageSize: number) => {
    if (!productId) {
      return;
    }

    setIsLoading(true);

    try {
      const clientId = getStoreId();
      const payload: InventorySearchPayload = {
        start: page * pageSize,
        length: pageSize,
        filter: {
          logic: 'AND',
          filters: [
            { field: 'ITEM_ID', operator: 'EQ', value: productId },
          ],
        },
      };

      const { data } = await postInventorySearch(clientId!, payload);

      if (!data.list) {
        setBatches([]);
        setIsLoading(false);
        return;
      }

      const items = data.list.map((item: InventoryItem, index: number) => ({
        ...item,
        rowItemId: `${item.itemId}-${item.areaId ? item.areaId : index - page}-${item.batchId}`,
      }));

      setBatches(items);
    } catch (e) {
      handleApiErrors(e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (productId) {
      getBatches(0, 1000);
    }
  }, [productId]);

  const onQtyAddHandler = (productItem: InventoryItem, qty: number) => {
    onAddItemToOrderHandler({
      batchId: productItem.batchId,
      areaId: productItem.areaId,
      qty: qty ?? 0,
      useScale: false,
      locationId: productItem.locationId,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
    >
      <ModalContainer>
        {isLoading && <Loader />}

        {!isLoading && (
          <>
            <TitleWrapper>
              <Typography variant="h6">{product.itemName ?? '-'}</Typography>
            </TitleWrapper>

            <BatchWrapper>
              {batches.map((batch) => (
                <BatchRow batch={batch} onQtyAddHandler={onQtyAddHandler} />
              ))}

              {!batches.length && (
                <Typography variant="body2">No batches found</Typography>
              )}
            </BatchWrapper>

            <CloseButton
              id="button-itemDetailsClose"
              onClick={onClose}
              size="small"
            >
              Close
            </CloseButton>
          </>
        )}
      </ModalContainer>
    </Dialog>
  );
};

export default BatchesModal;
